import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const STATUSES = {
  en_attente: { label: 'En attente', color: '#C05621', bg: 'rgba(246,173,85,0.15)' },
  en_cours: { label: 'En cours', color: '#2B6CB0', bg: 'rgba(66,153,225,0.15)' },
  resolu: { label: 'Résolu ✓', color: '#276749', bg: 'rgba(104,211,145,0.15)' },
  refuse: { label: 'Refusé', color: '#718096', bg: 'rgba(203,213,224,0.15)' },
};

const TYPES = {
  idee: { label: '💡 Idée', color: '#276749' },
  bug: { label: '🐛 Bug', color: '#9B2C2C' },
  amelioration: { label: '⚡ Amélioration', color: '#2B6CB0' },
};

export default function Admin() {
  const { user, token, API, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchStats = useCallback(async () => { const h = { Authorization: `Bearer ${token}` }; try { const res = await axios.get(`${API}/admin/stats`, { headers: h }); setStats(res.data); } catch (err) { console.error(err); } }, [API, token]);
  const fetchUsers = useCallback(async () => { const h = { Authorization: `Bearer ${token}` }; try { const res = await axios.get(`${API}/admin/users`, { headers: h }); setUsers(res.data); } catch (err) { console.error(err); } }, [API, token]);
  const fetchPosts = useCallback(async () => { const h = { Authorization: `Bearer ${token}` }; try { const res = await axios.get(`${API}/admin/posts`, { headers: h }); setPosts(res.data); } catch (err) { console.error(err); } }, [API, token]);
  const fetchSuggestions = useCallback(async () => { const h = { Authorization: `Bearer ${token}` }; try { const res = await axios.get(`${API}/suggestions`, { headers: h }); setSuggestions(res.data); } catch (err) { console.error(err); } }, [API, token]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) { navigate('/'); return; }
    Promise.all([fetchStats(), fetchUsers(), fetchPosts(), fetchSuggestions()]).finally(() => setLoading(false));
  }, [user, authLoading]);

  const handleBan = async (userId) => {
    try { const res = await axios.put(`${API}/admin/users/${userId}/ban`, {}, { headers: { Authorization: `Bearer ${token}` } }); setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_banned: res.data.banned } : u)); }
    catch (err) { console.error(err); }
  };

  const handleToggleAdmin = async (userId) => {
    if (!window.confirm('Modifier le statut admin ?')) return;
    try { const res = await axios.put(`${API}/admin/users/${userId}/admin`, {}, { headers: { Authorization: `Bearer ${token}` } }); setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: res.data.is_admin } : u)); }
    catch (err) { console.error(err); }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Supprimer définitivement @${username} ?`)) return;
    try { await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } }); setUsers(prev => prev.filter(u => u.id !== userId)); }
    catch (err) { console.error(err); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    try { await axios.delete(`${API}/admin/posts/${postId}`, { headers: { Authorization: `Bearer ${token}` } }); setPosts(prev => prev.filter(p => p.id !== postId)); }
    catch (err) { console.error(err); }
  };

  const handleSuggestionStatus = async (id, status) => {
    try { await axios.put(`${API}/suggestions/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } }); setSuggestions(prev => prev.map(s => s.id === id ? { ...s, status } : s)); }
    catch (err) { console.error(err); }
  };

  const handleDeleteSuggestion = async (id) => {
    if (!window.confirm('Supprimer cette suggestion ?')) return;
    try { await axios.delete(`${API}/suggestions/${id}`, { headers: { Authorization: `Bearer ${token}` } }); setSuggestions(prev => prev.filter(s => s.id !== id)); }
    catch (err) { console.error(err); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#8b949e', background: '#0d1117', minHeight: '100vh' }}>Chargement...</div>;

  const filteredUsers = users.filter(u => u.username.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()));
  const filteredPosts = posts.filter(p => p.content.toLowerCase().includes(search.toLowerCase()) || p.author.toLowerCase().includes(search.toLowerCase()));
  const filteredSuggestions = suggestions.filter(s => s.title.toLowerCase().includes(search.toLowerCase()) || s.description.toLowerCase().includes(search.toLowerCase()));

  const TABS = [
    { key: 'stats', label: '📊 Statistiques' },
    { key: 'users', label: `👥 Utilisateurs (${users.length})` },
    { key: 'posts', label: `📝 Posts (${posts.length})` },
    { key: 'suggestions', label: `💡 Suggestions (${suggestions.filter(s => s.status === 'en_attente').length} nouvelles)` },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117' }}>
      {/* Header */}
      <div style={{ background: '#161b22', borderBottom: '1px solid #30363d', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <Link to="/" style={{ color: '#58a6ff', fontSize: '13px', textDecoration: 'none' }}>← Retour</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>🛡️</span>
          <h1 style={{ fontSize: '18px', fontWeight: 800, color: '#e6edf3', margin: 0 }}>Dashboard Admin</h1>
        </div>
        <span style={{ fontSize: '12px', color: '#484f58', marginLeft: 'auto' }}>@<span style={{ color: '#58a6ff', fontWeight: 600 }}>{user.username}</span></span>
      </div>

      {/* Tabs */}
      <div style={{ background: '#161b22', borderBottom: '1px solid #30363d', padding: '0 24px', display: 'flex', gap: '0', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(''); }} style={{ background: 'none', border: 'none', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: tab === t.key ? '#58a6ff' : '#8b949e', borderBottom: `2px solid ${tab === t.key ? '#58a6ff' : 'transparent'}`, transition: 'all .2s', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '28px 24px' }}>

        {/* ── STATS ── */}
        {tab === 'stats' && stats && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' }}>
              {[
                { label: 'Utilisateurs', value: stats.total_users, sub: `+${stats.new_users_week} cette semaine`, icon: '👥', color: '#58a6ff' },
                { label: 'Posts', value: stats.total_posts, sub: `+${stats.new_posts_week} cette semaine`, icon: '📝', color: '#3fb950' },
                { label: 'Projets', value: stats.total_projects, sub: `+${stats.new_projects_week} cette semaine`, icon: '🚀', color: '#d29922' },
                { label: 'Messages', value: stats.total_messages, sub: 'Total envoyés', icon: '💬', color: '#bc8cff' },
                { label: 'Commentaires', value: stats.total_comments, sub: 'Total', icon: '💭', color: '#ff7b72' },
                { label: 'Likes', value: stats.total_likes, sub: 'Total', icon: '❤️', color: '#ff6b6b' },
                { label: 'Abonnements', value: stats.total_follows, sub: 'Relations', icon: '🤝', color: '#79c0ff' },
                { label: 'Bannis', value: stats.banned_users, sub: 'Comptes suspendus', icon: '🚫', color: '#f85149' },
              ].map((kpi, i) => (
                <div key={i} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#8b949e', letterSpacing: '0.5px' }}>{kpi.label.toUpperCase()}</span>
                    <span style={{ fontSize: '20px' }}>{kpi.icon}</span>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: kpi.color, lineHeight: 1, marginBottom: '4px' }}>{kpi.value}</div>
                  <div style={{ fontSize: '11px', color: '#484f58' }}>{kpi.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#e6edf3', marginBottom: '20px' }}>📈 Nouvelles inscriptions — 7 derniers jours</h3>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '120px' }}>
                {stats.users_per_day.map((day, i) => {
                  const max = Math.max(...stats.users_per_day.map(d => d.count), 1);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#58a6ff' }}>{day.count}</span>
                      <div style={{ width: '100%', height: `${Math.max((day.count / max) * 100, 4)}%`, background: '#1f6feb', borderRadius: '4px 4px 0 0', minHeight: '4px' }}></div>
                      <span style={{ fontSize: '10px', color: '#484f58' }}>{day.date}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#e6edf3', marginBottom: '16px' }}>🏆 Top projets par membres</h3>
              {stats.top_projects.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < stats.top_projects.length - 1 ? '1px solid #21262d' : 'none' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#484f58', width: '20px' }}>#{i + 1}</span>
                  <span style={{ fontSize: '18px' }}>{p.emoji || '🚀'}</span>
                  <Link to={`/projects/${p.id}`} style={{ flex: 1, fontSize: '14px', fontWeight: 600, color: '#58a6ff', textDecoration: 'none' }}>{p.title}</Link>
                  <span style={{ fontSize: '12px', color: '#8b949e' }}>par @{p.owner}</span>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#3fb950', background: 'rgba(63,185,80,0.1)', padding: '2px 8px', borderRadius: '10px' }}>{p.members} membres</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── USERS ── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par username ou email..."
                style={{ flex: 1, padding: '10px 16px', border: '1px solid #30363d', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#161b22', color: '#e6edf3' }}
                onFocus={e => e.target.style.borderColor = '#58a6ff'} onBlur={e => e.target.style.borderColor = '#30363d'}
              />
              <span style={{ fontSize: '13px', color: '#8b949e', flexShrink: 0 }}>{filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}</span>
            </div>
            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #30363d', background: '#0d1117' }}>
                    {['UTILISATEUR', 'EMAIL', 'POSTS', 'PROJETS', 'INSCRIT LE', 'STATUT', 'ACTIONS'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: h === 'POSTS' || h === 'PROJETS' || h === 'STATUT' || h === 'ACTIONS' ? 'center' : 'left', fontSize: '11px', fontWeight: 600, color: '#8b949e', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #21262d' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#1f6feb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{u.username.slice(0, 2).toUpperCase()}</div>
                          <div>
                            <Link to={`/profile/${u.username}`} style={{ fontSize: '13px', fontWeight: 600, color: '#58a6ff', textDecoration: 'none' }}>@{u.username}</Link>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                              {u.is_admin && <span style={{ fontSize: '10px', background: 'rgba(188,140,255,0.2)', color: '#bc8cff', padding: '1px 6px', borderRadius: '6px', fontWeight: 600 }}>ADMIN</span>}
                              {u.is_banned && <span style={{ fontSize: '10px', background: 'rgba(248,81,73,0.2)', color: '#f85149', padding: '1px 6px', borderRadius: '6px', fontWeight: 600 }}>BANNI</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#8b949e' }}>{u.email}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', color: '#e6edf3', fontWeight: 600 }}>{u.posts_count}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', color: '#e6edf3', fontWeight: 600 }}>{u.projects_count}</td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: '#8b949e' }}>{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '8px', background: u.is_banned ? 'rgba(248,81,73,0.15)' : 'rgba(63,185,80,0.15)', color: u.is_banned ? '#f85149' : '#3fb950' }}>
                          {u.is_banned ? 'Banni' : 'Actif'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {u.id !== user.id ? (
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button onClick={() => handleBan(u.id)} style={{ background: u.is_banned ? 'rgba(63,185,80,0.15)' : 'rgba(248,81,73,0.15)', color: u.is_banned ? '#3fb950' : '#f85149', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                              {u.is_banned ? '✓ Débannir' : '🚫 Bannir'}
                            </button>
                            <button onClick={() => handleToggleAdmin(u.id)} style={{ background: 'rgba(188,140,255,0.15)', color: '#bc8cff', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                              {u.is_admin ? '👑 -Admin' : '👑 +Admin'}
                            </button>
                            <button onClick={() => handleDeleteUser(u.id, u.username)} style={{ background: 'rgba(248,81,73,0.1)', color: '#f85149', border: 'none', padding: '5px 8px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>🗑️</button>
                          </div>
                        ) : <span style={{ fontSize: '11px', color: '#484f58', fontStyle: 'italic' }}>C'est toi</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── POSTS ── */}
        {tab === 'posts' && (
          <div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par contenu ou auteur..."
                style={{ flex: 1, padding: '10px 16px', border: '1px solid #30363d', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#161b22', color: '#e6edf3' }}
                onFocus={e => e.target.style.borderColor = '#58a6ff'} onBlur={e => e.target.style.borderColor = '#30363d'}
              />
              <span style={{ fontSize: '13px', color: '#8b949e', flexShrink: 0 }}>{filteredPosts.length} post{filteredPosts.length > 1 ? 's' : ''}</span>
            </div>
            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #30363d', background: '#0d1117' }}>
                    {['AUTEUR', 'CONTENU', 'TYPE', '❤️', '💬', 'DATE', 'ACTION'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#8b949e', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #21262d' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '12px 16px' }}><Link to={`/profile/${p.author}`} style={{ fontSize: '13px', fontWeight: 600, color: '#58a6ff', textDecoration: 'none' }}>@{p.author}</Link></td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#c9d1d9', maxWidth: '280px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.content}</div>
                        {p.media_url && <span style={{ fontSize: '11px', color: '#8b949e' }}>{p.media_type === 'image' ? '🖼️' : '🎬'}</span>}
                      </td>
                      <td style={{ padding: '12px 16px' }}><span style={{ fontSize: '11px', background: '#21262d', color: '#8b949e', padding: '2px 8px', borderRadius: '6px' }}>{p.post_type}</span></td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#e6edf3', fontWeight: 600 }}>{p.likes_count}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#e6edf3', fontWeight: 600 }}>{p.comments_count}</td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: '#8b949e' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <button onClick={() => handleDeletePost(p.id)} style={{ background: 'rgba(248,81,73,0.15)', color: '#f85149', border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>🗑️ Supprimer</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SUGGESTIONS ── */}
        {tab === 'suggestions' && (
          <div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                style={{ flex: 1, minWidth: '200px', padding: '10px 16px', border: '1px solid #30363d', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#161b22', color: '#e6edf3' }}
                onFocus={e => e.target.style.borderColor = '#58a6ff'} onBlur={e => e.target.style.borderColor = '#30363d'}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(STATUSES).map(([key, s]) => (
                  <span key={key} style={{ fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '10px', background: s.bg, color: s.color }}>
                    {s.label}: {suggestions.filter(sg => sg.status === key).length}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredSuggestions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#484f58' }}>Aucune suggestion</div>
              ) : filteredSuggestions.map(s => {
                const st = STATUSES[s.status] || STATUSES.en_attente;
                const t = TYPES[s.suggestion_type] || TYPES.idee;
                return (
                  <div key={s.id} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '18px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {/* Vote count */}
                      <div style={{ background: '#21262d', borderRadius: '8px', padding: '8px 12px', textAlign: 'center', minWidth: '48px' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#58a6ff' }}>{s.votes}</div>
                        <div style={{ fontSize: '10px', color: '#484f58' }}>votes</div>
                      </div>

                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: t.color }}>{t.label}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '8px', background: st.bg, color: st.color }}>{st.label}</span>
                          <span style={{ fontSize: '11px', color: '#484f58' }}>par @{s.author} · {new Date(s.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#e6edf3', marginBottom: '6px' }}>{s.title}</h3>
                        <p style={{ fontSize: '13px', color: '#8b949e', lineHeight: 1.6, margin: 0 }}>{s.description}</p>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <select value={s.status} onChange={e => handleSuggestionStatus(s.id, e.target.value)} style={{ background: '#21262d', color: '#e6edf3', border: '1px solid #30363d', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                          <option value="en_attente">En attente</option>
                          <option value="en_cours">En cours</option>
                          <option value="resolu">Résolu</option>
                          <option value="refuse">Refusé</option>
                        </select>
                        <button onClick={() => handleDeleteSuggestion(s.id)} style={{ background: 'rgba(248,81,73,0.15)', color: '#f85149', border: 'none', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>🗑️</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
